import { safeExternalHttpsUrl } from '../../domain/externalUrlPolicy';
import { escapeHtml } from './formatUtils';
import type { IdeaSummary } from './ideaDetailController';

export interface CommunityIdeaDetailRenderContext {
	surface: 'dialog' | 'page';
}

const thumb =
	'<svg aria-hidden="true" data-icon-source="lucide" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 10v12"></path><path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z"></path></svg>';
const sparkle =
	'<svg aria-hidden="true" data-icon-source="lucide" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m12 3-1.5 5.5L5 10l5.5 1.5L12 17l1.5-5.5L19 10l-5.5-1.5Z"></path></svg>';
const linkIcon =
	'<svg aria-hidden="true" data-icon-source="lucide" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path></svg>';
const attachmentIcon =
	'<svg aria-hidden="true" data-icon-source="lucide" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path></svg>';

function detailSection(label: string, content: string, className: string): string {
	return `<section class="community-detail-section ${className}"><h3>${label}</h3>${content || '<p class="community-detail-empty">—</p>'}</section>`;
}

function humanFileSize(size: number): string {
	if (!Number.isFinite(size) || size <= 0) return '0 B';

	const units = ['B', 'KB', 'MB', 'GB'];
	let value = size;
	let index = 0;
	while (value >= 1024 && index < units.length - 1) {
		value /= 1024;
		index++;
	}
	return `${value >= 10 || index === 0 ? Math.round(value) : value.toFixed(1)} ${units[index]}`;
}

export function renderCommunityIdeaDetail(summary: IdeaSummary, context: CommunityIdeaDetailRenderContext): string {
	const unavailable = summary.available === false || (summary.available === undefined && summary.publicIndexable === false);
	const selected = !unavailable && (summary.isAdopted || summary.displayStatus === 'Selected');
	const status = unavailable ? 'Unavailable' : selected ? 'Selected' : 'Voting';
	const liked = Boolean(summary.viewerLiked);
	const topicSlug = summary.navigableTopicSlug?.trim() ?? '';
	const hasTopic = selected && Boolean(topicSlug);
	const suffix = context.surface === 'dialog' ? '' : '-page';
	const support = !selected && !unavailable
		? `<div class="idea-detail-vote-group" aria-label="Your support"><button type="button" class="idea-detail-vote-btn ${liked ? 'voted' : ''}" id="ideaDetailSupportBtn${suffix}" data-idea-id="${escapeHtml(summary.id)}" data-proto-click="voteIdea('${escapeHtml(summary.id)}', this)" aria-label="${liked ? 'You supported this request' : 'Support this request'}">${thumb}<span class="community-support-sparkles">${sparkle.repeat(7)}</span></button></div>`
		: '';
	const topic =
		hasTopic
			? `<button type="button" class="btn btn-outline btn-sm community-detail-topic" data-proto-click="showCommunityIdeaTopic('${escapeHtml(topicSlug)}')">View Topic</button>`
			: '';
	const strengthen = (summary.strengthenItems ?? [])
		.filter(Boolean)
		.map((value) => `<li>${escapeHtml(value)}</li>`)
		.join('');
	const weaken = (summary.weakenItems ?? [])
		.filter(Boolean)
		.map((value) => `<li>${escapeHtml(value)}</li>`)
		.join('');
	const links = (summary.links ?? [])
		.map((value) => {
			const href = safeExternalHttpsUrl(value.url);
			return href
				? `<a class="community-detail-link" href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">${linkIcon}<span>${escapeHtml(value.label?.trim() || href)}</span></a>`
				: '';
		})
		.join('');
	const attachments = (summary.attachments ?? [])
		.map(
			(value) =>
				`<div class="community-detail-attachment">${attachmentIcon}<span class="community-detail-attachment-name">${escapeHtml(value.name)}</span><span class="community-detail-attachment-meta">${humanFileSize(value.size)} · ${escapeHtml(value.mimeType)}</span></div>`,
		)
		.join('');

	return `<article class="community-detail" data-idea-id="${escapeHtml(summary.id)}" data-status="${status.toLowerCase()}" data-has-topic="${String(hasTopic)}" data-has-vote-action="${String(!selected && !unavailable)}" data-is-adopted="${String(selected || unavailable)}" data-viewer-liked="${String(liked)}"><h2 class="community-detail-title">${escapeHtml(summary.title)}</h2>${summary.subtitle?.trim() ? `<p class="community-detail-subtitle">${escapeHtml(summary.subtitle)}</p>` : ''}<div class="community-detail-status-row"><span class="pill ${selected ? 'pill-completed' : unavailable ? 'pill-rejected' : 'pill-adopted'}">${status}</span>${support}${topic}</div>${detailSection('Strengthen', strengthen ? `<ul>${strengthen}</ul>` : '', 'community-detail-strengthen')}${detailSection('Weaken', weaken ? `<ul>${weaken}</ul>` : '', 'community-detail-weaken')}${detailSection('Reference Links', links, 'community-detail-links')}${detailSection('Attachments', attachments, 'community-detail-attachments')}</article>`;
}
